import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { MatSidenav } from '@angular/material/sidenav';
import { CrudModeEnum } from '../../../enum/crud-mode.enum';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { DEFAULT_SCROLL_SIZE } from '../../../../config/base-table.config';
import { map } from 'rxjs/operators';
import { Duplicate } from '../../../../utils/duplicate-utils';
import { ScrollManager } from '../../../../services/scroll-manager.service';
import { SnackbarService } from '../../../../services/snackbar.service';
import { AutocompleteService } from '../../../../services/scroll.service';
import { FilterFieldSpec, FilterFieldType, FilterSchema } from '../../../interfaces/filter-schema.interface';
import { safeMap } from '../../../../utils/array-utils';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'base-sidenav',
  templateUrl: './sidenav-base-filter.component.html',
})
export class SidenavBaseFilterComponent implements OnInit, AfterViewInit, OnDestroy {
  // Résout `resolve.service` (token de classe déclaré dans le schéma) sans imposer d'injection aux enfants
  private readonly injector = inject(Injector);
  @Input() sidenav: MatSidenav;
  @Input() standardForms: TemplateRef<any>;
  @Input() showValidate = true;
  @Output() onFilter = new EventEmitter<any>();
  @Output() onClear = new EventEmitter<any>();
  @Output() onValidate = new EventEmitter<any>();
  @Output() onClose = new EventEmitter<any>();
  subscription = new Subscription();
  mode: CrudModeEnum = CrudModeEnum.Filter;
  /** Filtres courants de la liste (ids/primitives), source de la restauration des chips (PRED-3054). */
  @Input() filters: any = {};
  /** Même objet que `filterSchema` du composant de liste : pilote la réduction et la restauration. */
  schema: FilterSchema = {};
  forms: { [key: string]: UntypedFormGroup } = {};

  constructor(
    protected snackbarService: SnackbarService,
    protected autocompleteService: AutocompleteService,
  ) {}

  ngOnInit() {
    this.initialize();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  /**
   * Après la vue seulement : `FormSelectBetweenComponent.ngOnInit` remet `comparison` à sa valeur par défaut,
   * un patch plus tôt serait écrasé. Différé d'une microtâche pour sortir du cycle de détection en cours
   * (patcher pendant ngAfterViewInit lève NG0100 sur les champs déjà vérifiés).
   */
  ngAfterViewInit() {
    Promise.resolve().then(() => this.restoreFromFilters());
  }

  initialize() {}

  /**
   * Reconstitue le formulaire depuis `filters` (ids/primitives, ex. restaurés de l'URL). Idempotent.
   * Ordre : scalaires → plages → tableaux (ticket : `outOfCatalog` active `genericObject` avant qu'on le remplisse).
   * `keys` permet un rappel ciblé quand une liste d'options arrive en différé (bill).
   */
  protected restoreFromFilters(keys: string[] = Object.keys(this.schema)): void {
    const entries = keys
      .map(key => [key, this.schema[key]] as const)
      .filter(([key, spec]) => spec && this.filters?.[key] !== null && this.filters?.[key] !== undefined);
    const ofKind = (...kinds: FilterFieldType[]) => entries.filter(([, spec]) => kinds.includes(spec.type));

    ofKind('text', 'number', 'boolean', 'option', 'option[]').forEach(([key, spec]) => this.restoreScalar(key, spec));
    ofKind('range').forEach(([key, spec]) => this.restoreRange(key, spec));
    ofKind('entity[]', 'value[]').forEach(([key, spec]) => this.restoreArray(key, spec));
  }

  /** Le contrôle tient des objets `{ value, label }` (select/multiple) : `option*`, ou un scalaire typé avec `options`. */
  private hasOptionObjects(spec: FilterFieldSpec): boolean {
    return spec.type === 'option' || spec.type === 'option[]' || !!spec.options;
  }

  private controlFor(apiKey: string, spec: FilterFieldSpec): AbstractControl | null {
    return this.forms[spec.form ?? 'standardForm']?.get(spec.control ?? apiKey) ?? null;
  }

  private restoreScalar(apiKey: string, spec: FilterFieldSpec): void {
    const control = this.controlFor(apiKey, spec);
    if (!control) {
      return;
    }
    const value = this.filters[apiKey];

    if (!this.hasOptionObjects(spec)) {
      control.patchValue(value);
      return;
    }
    // mat-select compare par référence : on retrouve les objets d'option existants par `.value`
    // (comparaison en string : une valeur typée côté API peut revenir de l'URL sous forme de chaîne).
    const options: any[] = (spec.options && (this as any)[spec.options]) ?? [];
    if (options.length === 0) {
      return; // options pas encore chargées : le composant rappellera restoreFromFilters([apiKey])
    }
    // Pas de `option.selected = true` : un flag posé sur la liste source survit à « Effacer tout » et
    // re-coche l'option ; le search-multiple statique lit la sélection dans la valeur du contrôle (PRED-3281).
    const wanted = new Set((Array.isArray(value) ? value : [value]).map(String));
    const matched = options.filter(option => wanted.has(String(option.value)));
    control.patchValue(spec.type === 'option[]' ? matched : (matched[0] ?? null));
  }

  private restoreRange(apiKey: string, spec: FilterFieldSpec): void {
    const form = spec.form ? this.forms[spec.form] : undefined;
    const range = this.filters[apiKey];
    if (!form || !range?.comparison) {
      return;
    }
    // `comparison` en premier : son valueChanges (dés)active min/max avant qu'on les pose
    form.patchValue({ comparison: range.comparison, min: range.min ?? null, max: range.max ?? null });
  }

  private restoreArray(apiKey: string, spec: FilterFieldSpec): void {
    const control = this.controlFor(apiKey, spec);
    if (!control) {
      return;
    }
    const raw = this.filters[apiKey];
    const values: string[] = (Array.isArray(raw) ? raw : [raw])
      .map(v => (v && typeof v === 'object' ? v._id : v))
      .filter(Boolean)
      .map(String);
    if (values.length === 0) {
      return;
    }
    const autocompleteKey = spec.resolve?.key ?? spec.control ?? apiKey;
    const display = spec.resolve?.displayProperty ?? 'label';
    const fallback = (id: string) => ({ _id: id, [display]: id, selected: true });

    if (spec.type === 'value[]') {
      const field = spec.valueField ?? '_id';
      const items = values.map(v => ({ _id: v, [field]: v, selected: true }));
      control.patchValue(items);
      this.autocompleteService.upsertItems(autocompleteKey, items, field);
      return;
    }

    // Liste statique d'entités tenue par le composant (ex. bill `owners`) : un `search-multiple` statique
    // n'affiche que les objets de SA liste → on patche ces objets (retrouvés par `_id`), sans requête.
    // Il en déduit la sélection depuis la valeur du contrôle : ne pas marquer `selected` sur la liste source (PRED-3281).
    if (spec.options) {
      const options: any[] = (this as any)[spec.options] ?? [];
      if (options.length === 0) {
        return; // liste pas encore chargée : le composant rappellera restoreFromFilters([apiKey])
      }
      const byId = new Map(options.map(option => [String(option._id), option]));
      const items = values.map(id => byId.get(id) ?? fallback(id));
      control.patchValue(items);
      return;
    }

    if (!spec.resolve) {
      const items = values.map(fallback);
      control.patchValue(items);
      this.autocompleteService.upsertItems(autocompleteKey, items);
      return;
    }

    const service = this.injector.get(spec.resolve.service);
    const toItem = spec.resolve.map ?? ((item: any) => item);
    this.subscription.add(
      service
        .resolveByIds(values)
        .pipe(catchError(() => of([])))
        .subscribe((found: any[]) => {
          const byId = new Map(found.map(item => [String(item._id), { ...toItem(item), selected: true }]));
          // id non résolu (entité archivée…) : chip visible et supprimable, libellée par son id
          const items = values.map(id => byId.get(id) ?? fallback(id));
          control.patchValue(items);
          this.autocompleteService.upsertItems(autocompleteKey, items);
        }),
    );
  }

  /** Échappatoire : un composant peut encore réduire lui-même ; `null` = réduction pilotée par `schema`. */
  buildCustomFilters(): Record<string, unknown> | null {
    return null;
  }

  protected reduceFromSchema(): Record<string, unknown> {
    const reduced: Record<string, unknown> = {};

    for (const [apiKey, spec] of Object.entries(this.schema)) {
      if (spec.type === 'range') {
        const rangeForm = spec.form ? this.forms[spec.form] : undefined;
        if (rangeForm) {
          reduced[apiKey] = { ...rangeForm.getRawValue() };
        }
        continue;
      }
      // Contrôle absent (code mort, input sous *ngIf) : rien n'est émis pour cette clé
      const control = this.forms[spec.form ?? 'standardForm']?.get(spec.control ?? apiKey);
      if (!control) {
        continue;
      }
      const value = control.value;

      switch (spec.type) {
        case 'option':
          reduced[apiKey] = spec.wrap === 'array' ? (value ? [value.value] : []) : value?.value;
          break;
        case 'option[]':
          reduced[apiKey] = safeMap(value, (x: any) => x.value);
          break;
        case 'entity[]':
          reduced[apiKey] = safeMap(value, (x: any) => x._id);
          break;
        case 'value[]':
          reduced[apiKey] = safeMap(value, (x: any) => x[spec.valueField ?? '_id']);
          break;
        default:
          // Scalaire typé (ex. boolean) présenté par un select d'options : on émet la valeur typée
          reduced[apiKey] = spec.options ? value?.value : value;
      }
    }

    return reduced;
  }

  validate() {
    const customFilters = this.buildCustomFilters() ?? this.reduceFromSchema();

    const queryString = new URLSearchParams(customFilters as Record<string, string>).toString();
    const url = `https://predik.eu.loclx.io/dossier/entite/numeroEntite?${queryString}`;
    const maxUrlLength = 2000;

    if (url.length > maxUrlLength) {
      this.snackbarService.danger(
        `Les filtres sont trop volumineux pour une requête GET (${url.length} caractères). Veuillez réduire les critères de recherche.`,
      );
      return;
    }

    this.filters = this.validateCustomFilters(customFilters);

    this.onFilter.emit(this.filters);

    this.close();
  }

  validateCustomFilters(customFilters: any): any {
    return Object.keys(customFilters).reduce((acc, key) => {
      const value = customFilters[key];

      if (this.isValidFilter(value, key)) {
        acc[key] = value;
      }

      return acc;
    }, {});
  }

  /**
   * Vérifie qu'un objet "de comparaison" est valide.
   * @param filter L'objet qui contient { comparison, min, max }.
   * @returns true si l'objet est valide, false sinon.
   */
  private isComparisonValid(filter: any): boolean {
    if (!filter) {
      return false;
    }

    switch (filter.comparison) {
      case 'between':
        return this.isNotNullOrEmpty(filter.min) && this.isNotNullOrEmpty(filter.max);

      case 'fixed':
      case 'greaterThan':
      case 'lessThan':
        return this.isNotNullOrEmpty(filter.max);

      default:
        return false;
    }
  }

  private isNotNullOrEmpty(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  isValidFilter(value: any, key: string): boolean {
    // 1) Cas value null/empty
    if (value === null || value === undefined || value === '') {
      return false;
    }

    // 2) Cas booléen
    if (typeof value === 'boolean') {
      return true;
    }

    // 3) Cas tableau
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    // 4) Cas objet
    if (typeof value === 'object') {
      if ('comparison' in value) {
        return this.isComparisonValid(value);
      } else {
        return Object.keys(value).some(k => value[k]);
      }
    }
    return true;
  }

  clear() {
    Object.keys(this.forms).forEach(formKey => {
      const form = this.forms[formKey];
      if (form instanceof UntypedFormGroup) {
        const isComparisonForm = 'comparison' in form.controls;
        Object.keys(form.controls).forEach(controlKey => {
          const control = form.controls[controlKey];
          const resetValue = isComparisonForm && controlKey === 'comparison' ? 'between' : '';
          control.enable({ emitEvent: false });
          control.setValue(resetValue);
          control.markAsPristine();
          control.markAsUntouched();
        });
      }
    });
    this.filters = {};
    this.onFilter.emit(this.filters);
    this.onClear.emit();
  }

  addForm(key: string, formGroup: UntypedFormGroup) {
    this.forms[key] = formGroup;
  }

  close() {
    this.sidenav.close();
  }

  getFormGroup(key: string) {
    return this.forms[key];
  }

  getItems$(key: string): Observable<any[]> {
    return this.autocompleteService.getItems$(key);
  }
}
